import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { getDateLocale } from '@/lib/i18n'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Link, router } from '@inertiajs/react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '../../../components/ui/alert-dialog'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command'
import { ArchiveIcon, ArchiveRestoreIcon, ArrowRightLeftIcon, LoaderIcon, BrainIcon, GlobeIcon, AlertTriangleIcon, CheckCircleIcon, XCircleIcon, RefreshCwIcon, ZapIcon, ScaleIcon, DatabaseIcon, ExternalLinkIcon, ShieldIcon, ShieldOffIcon, SparklesIcon, SettingsIcon, MoonIcon } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select'
import { Label } from '../../../components/ui/label'
import { Switch } from '../../../components/ui/switch'
import { toast } from 'sonner'

interface User {
  id: number
  email: string
  first_name: string
  last_name: string
}

interface Organization {
  id: number
  name: string
  slug: string
}

interface Message {
  id: number
  content: string
  role: string
  created_at: string
}

interface Conversation {
  id: number
  title: string
  status: string
  created_at: string
  messages: Message[]
}

interface DevInstance {
  id: number
  status: string
  runtime_state?: string
  instance_id: string
  ip_address: string
  created_at: string
  setup_error_log?: string
  setup_progress?: { step: string; message: string }
  production_status: string
  fly_ipv4?: string[]
  fly_ipv6?: string[]
  django_stack?: boolean
  project_type?: 'rails_app' | 'django_app' | 'managed_agent'
  capabilities?: {
    parallel_workers: boolean
    browser_verification: boolean
  }
  cost_savings_percentage?: number
}

interface DomainPurchase {
  id: number
  domain_name: string
  registration_type: 'purchased' | 'byod'
  purchase_status: 'pending' | 'processing' | 'completed' | 'failed' | 'expired'
  activation_status: 'not_activated' | 'configuring_dns' | 'activating_certificate' | 'active' | 'failed'
  expires_at?: string
  dns_verified_at?: string
  activated_at?: string
  activation_error?: string
  created_at: string
}

interface Project {
  id: number
  name: string
  is_archived: boolean
  archive_protected_until?: string | null
  archive_protected?: boolean
  created_at: string
  updated_at: string
  user: User
  organization?: Organization
  conversations: Conversation[]
  dev_instance?: DevInstance
  domain_purchase?: DomainPurchase
  claude_model?: string | null
  pm_model?: string | null
  cost_strategy?: string
  author_email: string
}

interface InstanceBackup {
  id: number
  status: string
  trigger: string
  s3_path: string
  size_bytes: number
  size_display: string
  manifest: Record<string, { size: number } | string | null>
  s3_console_url: string | null
  created_at: string
}

interface WeightClassification {
  weight: string
  range: string
  count: number
  pct_messages: number
  credits: number
  pct_credits: number
}

interface DailyBreakdown {
  date: string
  day_name: string
  messages: number
  credits: number
  light: number
  medium: number
  heavy: number
  mega: number
}

interface TopConversation {
  id: number
  title: string
  messages: number
  credits: number
  avg_per_msg: number
  status: string
}

interface UsageStats {
  period_days: number
  summary: {
    total_messages: number
    total_credits: number
    avg_credits_per_message: number
    total_cost_usd: number
  }
  weight_classification: WeightClassification[]
  daily_breakdown: DailyBreakdown[]
  top_conversations: TopConversation[]
}

interface AdminProjectShowProps {
  project: Project
  claude_models: Record<string, string>
  default_claude_model: string
  pm_models: Record<string, string>
  default_pm_model: string
  cost_strategies: Record<string, number>
  backup?: InstanceBackup | null
  usage_stats: UsageStats
  feature_defaults: {
    parallel_agents_enabled: boolean
    verification_enabled: boolean
  }
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
    notice?: string
  }
}

interface TransferOrganization {
  id: number
  name: string
}

const MODEL_ICONS: Record<string, typeof ZapIcon> = {
  'haiku': ZapIcon,
  'sonnet': ScaleIcon,
  'opus': BrainIcon,
  'gemini': SparklesIcon,
  'kimi': MoonIcon,
  'gpt': SparklesIcon,
}

function getModelIcon(modelId: string) {
  for (const [key, Icon] of Object.entries(MODEL_ICONS)) {
    if (modelId.includes(key)) return Icon
  }
  return ScaleIcon
}

const COST_STRATEGY_LABELS: Record<string, string> = {
  cost_default: 'Default (no optimization)',
  clear_every_message: 'Clear after every message',
  clear_after_tool_limit: 'Clear after tool limit',
}

function Show({ project, claude_models, default_claude_model, pm_models, default_pm_model, cost_strategies, backup, usage_stats, feature_defaults }: AdminProjectShowProps) {
  const { t } = useTranslation('admin')
  const [isArchiving, setIsArchiving] = useState(false)
  const [isRestoring, setIsRestoring] = useState(false)
  const [isTransferring, setIsTransferring] = useState(false)
  const [transferDialogOpen, setTransferDialogOpen] = useState(false)
  const [orgSearch, setOrgSearch] = useState('')
  const [organizations, setOrganizations] = useState<TransferOrganization[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [selectedOrg, setSelectedOrg] = useState<TransferOrganization | null>(null)
  const [isUpdatingModel, setIsUpdatingModel] = useState(false)
  const [isResettingConversation, setIsResettingConversation] = useState(false)
  const [isExtendingProtection, setIsExtendingProtection] = useState(false)
  const [projectFeatureDefaults, setProjectFeatureDefaults] = useState(feature_defaults)
  const [updatingFeatureKey, setUpdatingFeatureKey] = useState<'parallel_agents_enabled' | 'verification_enabled' | null>(null)
  const supportsFeatureSettings = Boolean(
    project.dev_instance?.capabilities?.parallel_workers ||
    project.dev_instance?.capabilities?.browser_verification
  )

  const csrfToken = () =>
    document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''

  useEffect(() => {
    setProjectFeatureDefaults(feature_defaults)
  }, [feature_defaults])

  const handleExtendProtection = (days: number) => {
    setIsExtendingProtection(true)
    router.post(`/admin/projects/${project.id}/extend_archival_protection`, { days }, {
      preserveScroll: true,
      onFinish: () => setIsExtendingProtection(false)
    })
  }

  const handleClearProtection = () => {
    setIsExtendingProtection(true)
    router.delete(`/admin/projects/${project.id}/clear_archival_protection`, {
      preserveScroll: true,
      onFinish: () => setIsExtendingProtection(false)
    })
  }

  const handleResetConversation = () => {
    setIsResettingConversation(true)
    router.post(`/admin/projects/${project.id}/reset_conversation`, {}, {
      onFinish: () => setIsResettingConversation(false)
    })
  }

  const handleModelChange = (value: string) => {
    setIsUpdatingModel(true)
    router.patch(`/admin/projects/${project.id}/update_model`, {
      claude_model: value === 'default' ? '' : value
    }, {
      preserveScroll: true,
      onFinish: () => setIsUpdatingModel(false)
    })
  }

  const [isUpdatingPmModel, setIsUpdatingPmModel] = useState(false)
  const handlePmModelChange = (value: string) => {
    setIsUpdatingPmModel(true)
    router.patch(`/admin/projects/${project.id}/update_pm_model`, {
      pm_model: value === 'default' ? '' : value
    }, {
      preserveScroll: true,
      onFinish: () => setIsUpdatingPmModel(false)
    })
  }

  const [isUpdatingCostStrategy, setIsUpdatingCostStrategy] = useState(false)
  const handleCostStrategyChange = (value: string) => {
    setIsUpdatingCostStrategy(true)
    router.patch(`/admin/projects/${project.id}/update_cost_strategy`, {
      cost_strategy: value
    }, {
      preserveScroll: true,
      onFinish: () => setIsUpdatingCostStrategy(false)
    })
  }

  const handleFeatureDefaultChange = async (key: 'parallel_agents_enabled' | 'verification_enabled', value: boolean) => {
    const previousDefaults = projectFeatureDefaults

    setUpdatingFeatureKey(key)
    setProjectFeatureDefaults(prev => ({
      ...prev,
      [key]: value,
    }))

    try {
      const response = await fetch(`/admin/projects/${project.id}/update_feature_defaults`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken(),
        },
        body: JSON.stringify({ [key]: value }),
      })
      const data = await response.json().catch(() => null)

      if (!response.ok) {
        throw new Error(data?.error || 'Failed to update feature setting')
      }

      setProjectFeatureDefaults(prev => ({
        ...prev,
        [key]: data?.[key] ?? value,
      }))
    } catch (error) {
      setProjectFeatureDefaults(previousDefaults)
      toast.error(error instanceof Error ? error.message : 'Failed to update feature setting')
    } finally {
      setUpdatingFeatureKey(null)
    }
  }

  // Debounced search for organizations
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (orgSearch.length === 0) {
        setOrganizations([])
        return
      }

      setIsSearching(true)
      try {
        const response = await fetch(`/admin/organizations/search?q=${encodeURIComponent(orgSearch)}`)
        const results = await response.json()
        setOrganizations(results)
      } catch (error) {
        console.error('Search failed:', error)
      } finally {
        setIsSearching(false)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [orgSearch])

  // Auto-refresh when restore or instance state change is in progress
  useEffect(() => {
    const runtimeState = project.dev_instance?.runtime_state
    if (
      project.dev_instance?.status === 'setup_in_progress' ||
      runtimeState === 'starting' ||
      runtimeState === 'stopping'
    ) {
      const interval = setInterval(() => {
        router.reload({ only: ['project'] })
      }, 5000)
      return () => clearInterval(interval)
    }
  }, [project.dev_instance?.status, project.dev_instance?.runtime_state])

  const handleArchive = () => {
    setIsArchiving(true)
    router.post(`/admin/projects/${project.id}/archive`, {}, {
      onFinish: () => setIsArchiving(false)
    })
  }

  const handleRestore = () => {
    setIsRestoring(true)
    router.post(`/admin/projects/${project.id}/unarchive`, {}, {
      onFinish: () => setIsRestoring(false)
    })
  }

  const handleTransfer = () => {
    if (!selectedOrg) return
    setIsTransferring(true)
    router.post(`/admin/projects/${project.id}/transfer`, {
      target_organization_id: selectedOrg.id
    }, {
      onFinish: () => {
        setIsTransferring(false)
        setTransferDialogOpen(false)
        setSelectedOrg(null)
        setOrgSearch('')
      }
    })
  }

  const getStatusBadge = (status: string) => {
    const statusVariants: Record<string, "success" | "warning" | "info" | "destructive" | "secondary"> = {
      'in_progress': 'info',
      'accepted': 'success',
      'discarded': 'secondary',
      'active': 'success',
      'provisioning': 'warning',
      'failed': 'destructive',
    }

    return (
      <Badge variant={statusVariants[status] || 'secondary'}>
        {status.replace('_', ' ')}
      </Badge>
    )
  }

  const getRuntimeBadge = (state: string) => {
    const variants: Record<string, "success" | "warning" | "info" | "destructive" | "secondary"> = {
      'running': 'success',
      'stopped': 'secondary',
      'starting': 'info',
      'stopping': 'warning',
    }
    return (
      <Badge variant={variants[state] || 'secondary'}>
        {t(`projects.show.devInstance.runtimeStates.${state}`)}
      </Badge>
    )
  }

  return (
    <>
      <AppHeader breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: t('projects.index.title'), href: '/admin/projects' }, { label: t('projects.show.title') }]} />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6">
        {/* Page Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">{project.name}</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('projects.show.description')}
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link href="/admin/projects">{t('projects.show.backToProjects')}</Link>
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Project Information */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="shadow-none">
              <CardHeader>
                <CardTitle>{t('projects.show.info.title')}</CardTitle>
                <CardDescription>{t('projects.show.info.description')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('projects.show.info.projectName')}</p>
                    <p className="mt-1 text-sm text-foreground">{project.name}</p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('projects.show.info.organization')}</p>
                    {project.organization ? (
                      <Link
                        href={`/admin/organizations/${project.organization.id}`}
                        className="mt-1 text-sm text-blue-600 hover:text-blue-900 hover:underline"
                      >
                        {project.organization.name}
                      </Link>
                    ) : (
                      <p className="mt-1 text-sm text-muted-foreground">{t('projects.show.info.none')}</p>
                    )}
                  </div>

                  <div className="col-span-2">
                    <p className="text-sm font-medium text-muted-foreground">{t('projects.show.info.author')}</p>
                    <div className="mt-1 flex items-center gap-2">
                      <div className="w-6 h-6 flex-shrink-0 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-semibold text-xs">
                        {project.author_email.substring(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm text-foreground">
                          {project.author_email}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('projects.show.info.created')}</p>
                    <p className="mt-1 text-sm text-foreground">
                      {new Date(project.created_at).toLocaleDateString(getDateLocale(), {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{t('projects.show.info.lastUpdated')}</p>
                    <p className="mt-1 text-sm text-foreground">
                      {new Date(project.updated_at).toLocaleDateString(getDateLocale(), {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Dev Instance */}
            {project.dev_instance && (
              <Card className="shadow-none">
                <CardHeader>
                  <CardTitle>{t('projects.show.devInstance.title')}</CardTitle>
                  <CardDescription>{t('projects.show.devInstance.description')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('projects.show.devInstance.status')}</p>
                      <div className="mt-1">
                        {getStatusBadge(project.dev_instance.status)}
                      </div>
                    </div>

                    {project.dev_instance.runtime_state && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">{t('projects.show.devInstance.runtimeState')}</p>
                        <div className="mt-1">
                          {getRuntimeBadge(project.dev_instance.runtime_state)}
                        </div>
                      </div>
                    )}

                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('projects.show.devInstance.ipAddress')}</p>
                      <p className="mt-1 text-sm text-foreground font-mono">
                        {project.dev_instance.ip_address || t('projects.show.devInstance.notAssigned')}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('projects.show.devInstance.instanceId')}</p>
                      <p className="mt-1 text-sm text-foreground font-mono">
                        {project.dev_instance.instance_id || '-'}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('projects.show.devInstance.created')}</p>
                      <p className="mt-1 text-sm text-foreground">
                        {new Date(project.dev_instance.created_at).toLocaleDateString(getDateLocale(), {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>

                    {project.dev_instance.production_status === 'deployed' && (
                      <>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">{t('projects.show.devInstance.flyIpv4')}</p>
                          <p className="mt-1 text-sm text-foreground font-mono">
                            {project.dev_instance.fly_ipv4?.length ? project.dev_instance.fly_ipv4.join(', ') : '-'}
                          </p>
                        </div>

                        <div>
                          <p className="text-sm font-medium text-muted-foreground">{t('projects.show.devInstance.flyIpv6')}</p>
                          <p className="mt-1 text-sm text-foreground font-mono break-all">
                            {project.dev_instance.fly_ipv6?.length ? project.dev_instance.fly_ipv6.join(', ') : '-'}
                          </p>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Start/Stop Controls */}
                  {project.dev_instance.runtime_state && (
                    <div className="border-t border-border pt-4">
                      {project.dev_instance.runtime_state === 'stopped' ? (
                        <Button
                          className="w-full bg-green-600 hover:bg-green-700"
                          onClick={() => router.post(`/admin/dev_instances/${project.dev_instance!.id}/start`)}
                        >
                          {t('projects.show.devInstance.actions.start')}
                        </Button>
                      ) : project.dev_instance.runtime_state === 'running' ? (
                        <Button
                          variant="outline"
                          className="w-full border-orange-300 text-orange-700 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-400 dark:hover:bg-orange-950"
                          onClick={() => router.post(`/admin/dev_instances/${project.dev_instance!.id}/stop`)}
                        >
                          {t('projects.show.devInstance.actions.stop', { percentage: project.dev_instance.cost_savings_percentage || 80 })}
                        </Button>
                      ) : (
                        <div className="flex items-center justify-center gap-2 py-2 text-sm text-muted-foreground">
                          <LoaderIcon className="h-4 w-4 animate-spin" />
                          {t(`projects.show.devInstance.runtimeStates.${project.dev_instance.runtime_state}`)}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Custom Domain */}
            {project.domain_purchase && (
              <Card className="shadow-none">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <GlobeIcon className="h-5 w-5 text-muted-foreground" />
                    <CardTitle>{t('projects.show.domain.title')}</CardTitle>
                  </div>
                  <CardDescription>{t('projects.show.domain.description')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <p className="text-sm font-medium text-muted-foreground">{t('projects.show.domain.domainName')}</p>
                      <p className="mt-1 text-lg font-semibold text-foreground font-mono">
                        {project.domain_purchase.domain_name}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('projects.show.domain.registrationType')}</p>
                      <div className="mt-1">
                        <Badge variant={project.domain_purchase.registration_type === 'purchased' ? 'info' : 'secondary'}>
                          {t(`projects.show.domain.registrationTypes.${project.domain_purchase.registration_type}`)}
                        </Badge>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('projects.show.domain.purchaseStatus')}</p>
                      <div className="mt-1">
                        <Badge variant={
                          project.domain_purchase.purchase_status === 'completed' ? 'success' :
                          project.domain_purchase.purchase_status === 'failed' || project.domain_purchase.purchase_status === 'expired' ? 'destructive' :
                          project.domain_purchase.purchase_status === 'processing' ? 'info' : 'warning'
                        }>
                          {t(`projects.show.domain.purchaseStatuses.${project.domain_purchase.purchase_status}`)}
                        </Badge>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('projects.show.domain.activationStatus')}</p>
                      <div className="mt-1">
                        <Badge variant={
                          project.domain_purchase.activation_status === 'active' ? 'success' :
                          project.domain_purchase.activation_status === 'failed' ? 'destructive' :
                          project.domain_purchase.activation_status === 'configuring_dns' || project.domain_purchase.activation_status === 'activating_certificate' ? 'info' : 'secondary'
                        }>
                          {t(`projects.show.domain.activationStatuses.${project.domain_purchase.activation_status}`)}
                        </Badge>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('projects.show.domain.dnsVerified')}</p>
                      <div className="mt-1 flex items-center gap-1">
                        {project.domain_purchase.dns_verified_at ? (
                          <>
                            <CheckCircleIcon className="h-4 w-4 text-green-500" />
                            <span className="text-sm text-green-600">{t('projects.show.domain.yes')}</span>
                          </>
                        ) : (
                          <>
                            <XCircleIcon className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">{t('projects.show.domain.no')}</span>
                          </>
                        )}
                      </div>
                    </div>

                    {project.domain_purchase.expires_at && (
                      <div className="col-span-2">
                        <p className="text-sm font-medium text-muted-foreground">{t('projects.show.domain.expiresAt')}</p>
                        <div className="mt-1 flex items-center gap-2">
                          <p className="text-sm text-foreground">
                            {new Date(project.domain_purchase.expires_at).toLocaleDateString(getDateLocale(), {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                          {(() => {
                            const daysUntilExpiry = Math.floor((new Date(project.domain_purchase.expires_at!).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
                            if (daysUntilExpiry < 30) {
                              return (
                                <Badge variant="warning" className="flex items-center gap-1">
                                  <AlertTriangleIcon className="h-3 w-3" />
                                  {t('projects.show.domain.expiringSoon', { days: daysUntilExpiry })}
                                </Badge>
                              )
                            }
                            return null
                          })()}
                        </div>
                      </div>
                    )}
                  </div>

                  {project.domain_purchase.activation_error && (
                    <div className="border-t border-border pt-4">
                      <p className="text-sm font-medium text-destructive mb-2">{t('projects.show.domain.activationError')}</p>
                      <pre className="text-xs bg-destructive/10 border border-destructive/30 rounded p-3 overflow-x-auto">
                        {project.domain_purchase.activation_error}
                      </pre>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Backup Info (shown for archived projects) */}
            {project.is_archived && backup && (
              <Card className="shadow-none">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <DatabaseIcon className="h-5 w-5 text-muted-foreground" />
                    <CardTitle>{t('projects.show.backup.title')}</CardTitle>
                  </div>
                  <CardDescription>{t('projects.show.backup.description')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('projects.show.backup.status')}</p>
                      <div className="mt-1">
                        <Badge variant={backup.status === 'completed' ? 'success' : backup.status === 'failed' ? 'destructive' : 'warning'}>
                          {backup.status}
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('projects.show.backup.size')}</p>
                      <p className="mt-1 text-sm text-foreground">{backup.size_display}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('projects.show.backup.createdAt')}</p>
                      <p className="mt-1 text-sm text-foreground">
                        {new Date(backup.created_at).toLocaleDateString(getDateLocale(), {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">{t('projects.show.backup.trigger')}</p>
                      <p className="mt-1 text-sm text-foreground capitalize">{backup.trigger}</p>
                    </div>
                  </div>

                  {backup.manifest && Object.keys(backup.manifest).length > 0 && (
                    <div className="border-t border-border pt-4">
                      <p className="text-sm font-medium text-muted-foreground mb-2">{t('projects.show.backup.components')}</p>
                      <div className="space-y-1">
                        {Object.entries(backup.manifest)
                          .filter((entry): entry is [string, { size: number }] => entry[1] != null && typeof entry[1] === 'object')
                          .map(([name, info]) => (
                          <div key={name} className="flex items-center justify-between py-1 px-2 rounded bg-muted/50 text-sm">
                            <span className="font-mono text-xs">{name}</span>
                            <span className="text-muted-foreground text-xs">
                              {info.size ? `${(info.size / 1024).toFixed(1)} KB` : '-'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {backup.s3_console_url && (
                    <div className="border-t border-border pt-4">
                      <a
                        href={backup.s3_console_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        <ExternalLinkIcon className="h-3.5 w-3.5" />
                        {t('projects.show.backup.viewInS3')}
                      </a>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Usage Stats */}
            {usage_stats.summary.total_messages > 0 && (
              <Card className="shadow-none">
                <CardHeader>
                  <CardTitle>{t('projects.show.usageStats.title', { days: usage_stats.period_days })}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Summary */}
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-muted-foreground">{t('projects.show.usageStats.summary.totalMessages')}</p>
                      <p className="mt-1 text-xl font-semibold">{usage_stats.summary.total_messages}</p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-muted-foreground">{t('projects.show.usageStats.summary.totalCredits')}</p>
                      <p className="mt-1 text-xl font-semibold">{usage_stats.summary.total_credits}</p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-muted-foreground">{t('projects.show.usageStats.summary.avgCreditsPerMessage')}</p>
                      <p className="mt-1 text-xl font-semibold">{usage_stats.summary.avg_credits_per_message}</p>
                    </div>
                    <div className="rounded-lg border p-3">
                      <p className="text-xs text-muted-foreground">{t('projects.show.usageStats.summary.totalCostUsd')}</p>
                      <p className="mt-1 text-xl font-semibold">${usage_stats.summary.total_cost_usd}</p>
                    </div>
                  </div>

                  {/* Weight Classification */}
                  <div>
                    <h4 className="mb-2 text-sm font-medium text-muted-foreground">{t('projects.show.usageStats.weightClassification.title')}</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="px-3 py-2 text-left font-medium text-muted-foreground">{t('projects.show.usageStats.weightClassification.weight')}</th>
                            <th className="px-3 py-2 text-left font-medium text-muted-foreground">{t('projects.show.usageStats.weightClassification.range')}</th>
                            <th className="px-3 py-2 text-right font-medium text-muted-foreground">{t('projects.show.usageStats.weightClassification.count')}</th>
                            <th className="px-3 py-2 text-right font-medium text-muted-foreground">{t('projects.show.usageStats.weightClassification.pctMessages')}</th>
                            <th className="px-3 py-2 text-right font-medium text-muted-foreground">{t('projects.show.usageStats.weightClassification.credits')}</th>
                            <th className="px-3 py-2 text-right font-medium text-muted-foreground">{t('projects.show.usageStats.weightClassification.pctCredits')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {usage_stats.weight_classification.map((w) => (
                            <tr key={w.weight} className="border-b">
                              <td className="px-3 py-2">
                                <Badge variant={w.weight === 'mega' ? 'destructive' : w.weight === 'heavy' ? 'default' : 'secondary'} className="text-xs">
                                  {t(`projects.show.usageStats.weightClassification.${w.weight}`)}
                                </Badge>
                              </td>
                              <td className="px-3 py-2 text-muted-foreground">{w.range}</td>
                              <td className="px-3 py-2 text-right">{w.count}</td>
                              <td className="px-3 py-2 text-right text-muted-foreground">{w.pct_messages}%</td>
                              <td className="px-3 py-2 text-right font-medium">{w.credits}</td>
                              <td className="px-3 py-2 text-right text-muted-foreground">{w.pct_credits}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Daily Breakdown */}
                  {usage_stats.daily_breakdown.length > 0 && (
                    <div>
                      <h4 className="mb-2 text-sm font-medium text-muted-foreground">{t('projects.show.usageStats.dailyBreakdown.title')}</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-muted/50">
                              <th className="px-3 py-2 text-left font-medium text-muted-foreground">{t('projects.show.usageStats.dailyBreakdown.day')}</th>
                              <th className="px-3 py-2 text-right font-medium text-muted-foreground">{t('projects.show.usageStats.dailyBreakdown.messages')}</th>
                              <th className="px-3 py-2 text-right font-medium text-muted-foreground">{t('projects.show.usageStats.dailyBreakdown.credits')}</th>
                              <th className="px-3 py-2 text-right font-medium text-green-600">L</th>
                              <th className="px-3 py-2 text-right font-medium text-yellow-600">M</th>
                              <th className="px-3 py-2 text-right font-medium text-orange-600">H</th>
                              <th className="px-3 py-2 text-right font-medium text-red-600">X</th>
                            </tr>
                          </thead>
                          <tbody>
                            {usage_stats.daily_breakdown.map((d) => (
                              <tr key={d.date} className="border-b">
                                <td className="px-3 py-2 whitespace-nowrap">{d.day_name} {d.date}</td>
                                <td className="px-3 py-2 text-right">{d.messages}</td>
                                <td className="px-3 py-2 text-right font-medium">{d.credits}</td>
                                <td className="px-3 py-2 text-right text-green-600">{d.light || ''}</td>
                                <td className="px-3 py-2 text-right text-yellow-600">{d.medium || ''}</td>
                                <td className="px-3 py-2 text-right text-orange-600">{d.heavy || ''}</td>
                                <td className="px-3 py-2 text-right text-red-600">{d.mega || ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Top Conversations */}
                  {usage_stats.top_conversations.length > 0 && (
                    <div>
                      <h4 className="mb-2 text-sm font-medium text-muted-foreground">{t('projects.show.usageStats.topConversations.title')}</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-muted/50">
                              <th className="px-3 py-2 text-left font-medium text-muted-foreground">{t('projects.show.usageStats.topConversations.conversation')}</th>
                              <th className="px-3 py-2 text-right font-medium text-muted-foreground">{t('projects.show.usageStats.topConversations.messages')}</th>
                              <th className="px-3 py-2 text-right font-medium text-muted-foreground">{t('projects.show.usageStats.topConversations.credits')}</th>
                              <th className="px-3 py-2 text-right font-medium text-muted-foreground">{t('projects.show.usageStats.topConversations.avgPerMsg')}</th>
                              <th className="px-3 py-2 text-left font-medium text-muted-foreground">{t('projects.show.usageStats.topConversations.status')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {usage_stats.top_conversations.map((c) => (
                              <tr key={c.id} className="border-b">
                                <td className="px-3 py-2 max-w-[200px] truncate" title={c.title}>{c.title}</td>
                                <td className="px-3 py-2 text-right">{c.messages}</td>
                                <td className="px-3 py-2 text-right font-medium">{c.credits}</td>
                                <td className="px-3 py-2 text-right text-muted-foreground">{c.avg_per_msg}</td>
                                <td className="px-3 py-2">{getStatusBadge(c.status)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Conversations/Features */}
            <Card className="shadow-none">
              <CardHeader>
                <CardTitle>{t('projects.show.features.title')}</CardTitle>
                <CardDescription>
                  {t('projects.show.features.description', { count: project.conversations.length })}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {project.conversations.length > 0 ? (
                  <div className="space-y-3">
                    {project.conversations.map((conversation) => (
                      <div
                        key={conversation.id}
                        className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-foreground">{conversation.title || t('projects.show.features.untitledFeature')}</p>
                            {getStatusBadge(conversation.status)}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {t('projects.show.features.messages', { count: conversation.messages.length })} •
                            {new Date(conversation.created_at).toLocaleDateString(getDateLocale())}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    {t('projects.show.features.empty')}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card className="shadow-none">
              <CardHeader>
                <CardTitle>{t('projects.show.actions.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button variant="outline" className="w-full justify-start" asChild>
                  <Link href={`/projects/${project.id}`}>{t('projects.show.actions.viewProject')}</Link>
                </Button>
                {project.dev_instance && (
                  <Button variant="outline" className="w-full justify-start" asChild>
                    <Link href={`/admin/dev_instances/${project.dev_instance.id}`}>{t('projects.show.actions.viewDevInstance')}</Link>
                  </Button>
                )}
                {project.dev_instance?.django_stack && (
                  <Button variant="outline" className="w-full justify-start" asChild>
                    <Link href={`/admin/django_infra/${project.dev_instance.id}`}>{t('projects.show.actions.viewDjangoInfra')}</Link>
                  </Button>
                )}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-green-600 hover:text-green-700 hover:bg-green-50"
                      disabled={isResettingConversation}
                    >
                      <RefreshCwIcon className="mr-2 h-4 w-4" />
                      {isResettingConversation ? t('projects.show.actions.resettingConversation') : t('projects.show.actions.resetConversation')}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader className="items-center">
                      <div className="bg-green-100 mx-auto mb-2 flex size-12 items-center justify-center rounded-full">
                        <RefreshCwIcon className="text-green-600 size-6" />
                      </div>
                      <AlertDialogTitle>{t('projects.show.resetConversationDialog.title')}</AlertDialogTitle>
                      <AlertDialogDescription className="text-center">
                        {t('projects.show.resetConversationDialog.description')}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t('projects.show.resetConversationDialog.cancel')}</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={handleResetConversation}
                        disabled={isResettingConversation}
                      >
                        {isResettingConversation ? t('projects.show.actions.resettingConversation') : t('projects.show.resetConversationDialog.confirm')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                {!project.is_archived && (
                  <div className="border border-green-200 rounded-md p-3 space-y-2 bg-green-50">
                    <div className="flex items-center gap-2">
                      <ShieldIcon className="h-4 w-4 text-green-600 shrink-0" />
                      <span className="text-sm font-medium text-green-800">{t('projects.show.archivalProtection.title')}</span>
                    </div>
                    <p className="text-xs text-green-700">
                      {project.archive_protected && project.archive_protected_until
                        ? t('projects.show.archivalProtection.protectedUntil', {
                            date: new Date(project.archive_protected_until).toLocaleDateString(getDateLocale(), { year: 'numeric', month: 'long', day: 'numeric' })
                          })
                        : t('projects.show.archivalProtection.notProtected')}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {([7, 30, 90] as const).map((days) => (
                        <Button
                          key={days}
                          variant="outline"
                          size="sm"
                          className="text-xs h-7 border-green-300 text-green-700 hover:bg-green-100"
                          disabled={isExtendingProtection}
                          onClick={() => handleExtendProtection(days)}
                        >
                          {t(`projects.show.archivalProtection.extend${days}`)}
                        </Button>
                      ))}
                    </div>
                    {project.archive_protected && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-xs h-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                        disabled={isExtendingProtection}
                        onClick={handleClearProtection}
                      >
                        <ShieldOffIcon className="mr-1 h-3 w-3" />
                        {t('projects.show.archivalProtection.clear')}
                      </Button>
                    )}
                  </div>
                )}
                {!project.is_archived && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                        disabled={isArchiving}
                      >
                        <ArchiveIcon className="mr-2 h-4 w-4" />
                        {isArchiving ? t('projects.show.actions.archiving') : t('projects.show.actions.archive')}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader className="items-center">
                        <div className="bg-amber-100 mx-auto mb-2 flex size-12 items-center justify-center rounded-full">
                          <ArchiveIcon className="text-amber-600 size-6" />
                        </div>
                        <AlertDialogTitle>{t('projects.show.archiveDialog.title', { name: project.name })}</AlertDialogTitle>
                        <AlertDialogDescription className="text-center">
                          {t('projects.show.archiveDialog.description')}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t('projects.show.archiveDialog.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                          onClick={handleArchive}
                          disabled={isArchiving}
                        >
                          {isArchiving ? t('projects.show.actions.archiving') : t('projects.show.archiveDialog.confirm')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                {project.is_archived && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="default"
                        className="w-full justify-start bg-blue-600 hover:bg-blue-700"
                        disabled={isRestoring || project.dev_instance?.status === 'setup_in_progress'}
                      >
                        <ArchiveRestoreIcon className="mr-2 h-4 w-4" />
                        {isRestoring || project.dev_instance?.status === 'setup_in_progress' ? t('projects.show.actions.restoring') : t('projects.show.actions.restore')}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader className="items-center">
                        <div className="bg-blue-100 mx-auto mb-2 flex size-12 items-center justify-center rounded-full">
                          <ArchiveRestoreIcon className="text-blue-600 size-6" />
                        </div>
                        <AlertDialogTitle>{t('projects.show.restoreDialog.title', { name: project.name })}</AlertDialogTitle>
                        <AlertDialogDescription className="text-center">
                          {t('projects.show.restoreDialog.description')}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t('projects.show.restoreDialog.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          onClick={handleRestore}
                          disabled={isRestoring || project.dev_instance?.status === 'setup_in_progress'}
                        >
                          {isRestoring || project.dev_instance?.status === 'setup_in_progress' ? t('projects.show.actions.restoring') : t('projects.show.restoreDialog.confirm')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                <AlertDialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    >
                      <ArrowRightLeftIcon className="mr-2 h-4 w-4" />
                      {t('projects.show.actions.transfer')}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader className="items-center">
                      <div className="bg-blue-100 mx-auto mb-2 flex size-12 items-center justify-center rounded-full">
                        <ArrowRightLeftIcon className="text-blue-600 size-6" />
                      </div>
                      <AlertDialogTitle>{t('projects.show.transferDialog.title', { name: project.name })}</AlertDialogTitle>
                      <AlertDialogDescription className="text-center">
                        {t('projects.show.transferDialog.description')}
                        {project.organization && (
                          <span className="block mt-1">
                            {t('projects.show.transferDialog.currentOrg')} <strong>{project.organization.name}</strong>
                          </span>
                        )}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="py-4">
                      {selectedOrg ? (
                        <div className="flex items-center justify-between p-3 border rounded-md bg-muted/50">
                          <span>{selectedOrg.name} <span className="text-muted-foreground">(#{selectedOrg.id})</span></span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedOrg(null)
                              setOrgSearch('')
                            }}
                          >
                            {t('projects.show.actions.change')}
                          </Button>
                        </div>
                      ) : (
                        <Command className="border rounded-md" shouldFilter={false}>
                          <CommandInput
                            placeholder={t('projects.show.transferDialog.search')}
                            value={orgSearch}
                            onValueChange={setOrgSearch}
                          />
                          <CommandList className="max-h-[200px]">
                            {isSearching ? (
                              <div className="py-6 text-center text-sm">
                                <LoaderIcon className="mx-auto h-4 w-4 animate-spin" />
                              </div>
                            ) : orgSearch.length === 0 ? (
                              <div className="py-6 text-center text-sm text-muted-foreground">
                                {t('projects.show.transferDialog.typeToSearch')}
                              </div>
                            ) : organizations.length === 0 ? (
                              <CommandEmpty>{t('projects.show.transferDialog.noOrgFound')}</CommandEmpty>
                            ) : (
                              <CommandGroup>
                                {organizations.map((org) => (
                                  <CommandItem
                                    key={org.id}
                                    value={org.name}
                                    onSelect={() => {
                                      setSelectedOrg(org)
                                      setOrgSearch('')
                                    }}
                                  >
                                    {org.name} <span className="text-muted-foreground">(#{org.id})</span>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            )}
                          </CommandList>
                        </Command>
                      )}
                    </div>
                    <AlertDialogFooter>
                      <AlertDialogCancel onClick={() => {
                        setSelectedOrg(null)
                        setOrgSearch('')
                      }}>
                        {t('projects.show.transferDialog.cancel')}
                      </AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={handleTransfer}
                        disabled={!selectedOrg || isTransferring}
                      >
                        {isTransferring ? t('projects.show.actions.transferring') : t('projects.show.transferDialog.confirm')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </CardContent>
            </Card>

            <Card className="shadow-none">
              <CardHeader>
                <CardTitle>{t('projects.show.statistics.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('projects.show.statistics.totalFeatures')}</p>
                  <p className="mt-1 text-2xl font-semibold text-foreground">
                    {project.conversations.length}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('projects.show.statistics.totalMessages')}</p>
                  <p className="mt-1 text-2xl font-semibold text-foreground">
                    {project.conversations.reduce((sum, conv) => sum + conv.messages.length, 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('projects.show.statistics.projectAge')}</p>
                  <p className="mt-1 text-sm text-foreground">
                    {t('projects.show.statistics.days', { count: Math.floor((Date.now() - new Date(project.created_at).getTime()) / (1000 * 60 * 60 * 24)) })}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-none">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <BrainIcon className="h-5 w-5 text-muted-foreground" />
                  <CardTitle>{t('projects.show.aiModel.title')}</CardTitle>
                </div>
                <CardDescription>
                  {t('projects.show.aiModel.description')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Select
                  value={project.claude_model || 'default'}
                  onValueChange={handleModelChange}
                  disabled={isUpdatingModel}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t('projects.show.aiModel.placeholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">
                      <span className="flex items-center gap-2">
                        <ScaleIcon className="h-4 w-4" />
                        {t('projects.show.aiModel.default')}
                      </span>
                    </SelectItem>
                    {Object.entries(claude_models)
                      .filter(([modelId]) => modelId !== default_claude_model)
                      .map(([modelId, modelName]) => {
                        const Icon = getModelIcon(modelId)
                        return (
                          <SelectItem key={modelId} value={modelId}>
                            <span className="flex items-center gap-2">
                              <Icon className="h-4 w-4" />
                              {modelName}
                            </span>
                          </SelectItem>
                        )
                      })}
                  </SelectContent>
                </Select>
                {project.claude_model && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    {t('projects.show.aiModel.overrideActive')}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="shadow-none">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <BrainIcon className="h-5 w-5 text-muted-foreground" />
                  <CardTitle>PM Model</CardTitle>
                </div>
                <CardDescription>
                  Model used for discussion and prototype conversations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Select
                  value={project.pm_model || 'default'}
                  onValueChange={handlePmModelChange}
                  disabled={isUpdatingPmModel}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select PM model..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default ({pm_models[default_pm_model] || default_pm_model})</SelectItem>
                    {Object.entries(pm_models).map(([modelId, modelName]) => (
                      <SelectItem key={modelId} value={modelId}>{modelName}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {project.pm_model && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    This project overrides the organization's PM model setting.
                  </p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle>Cost Strategy</CardTitle>
                <CardDescription>
                  Controls context clearing between messages to reduce API costs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Select
                  value={project.cost_strategy || 'cost_default'}
                  onValueChange={handleCostStrategyChange}
                  disabled={isUpdatingCostStrategy}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(cost_strategies).map(([name]) => (
                      <SelectItem key={name} value={name}>
                        {COST_STRATEGY_LABELS[name] || name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {project.cost_strategy && project.cost_strategy !== 'cost_default' && (
                  <p className="mt-2 text-xs text-amber-600">
                    Cost optimization active — context will be cleared between messages
                  </p>
                )}
              </CardContent>
            </Card>

            {supportsFeatureSettings && (
              <Card className="shadow-none">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <SettingsIcon className="h-5 w-5 text-muted-foreground" />
                    <CardTitle>Feature Settings</CardTitle>
                  </div>
                  <CardDescription>
                    Configure feature behavior for this project
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-medium">Parallel Agents</Label>
                      <p className="text-xs text-muted-foreground">
                        Workers build tasks on feature branches in parallel
                      </p>
                    </div>
                    <Switch
                      checked={projectFeatureDefaults.parallel_agents_enabled}
                      onCheckedChange={(checked) => handleFeatureDefaultChange('parallel_agents_enabled', checked)}
                      disabled={updatingFeatureKey !== null}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-medium">Verification (Browser Use)</Label>
                      <p className="text-xs text-muted-foreground">
                        Pilot uses browser automation to verify generated code
                      </p>
                    </div>
                    <Switch
                      checked={projectFeatureDefaults.verification_enabled}
                      onCheckedChange={(checked) => handleFeatureDefaultChange('verification_enabled', checked)}
                      disabled={updatingFeatureKey !== null}
                    />
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}




Show.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Show
